import React from 'react';
import { Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const Cart = () => {
  const { cart, loading, updateQuantity, removeFromCart } = useCart();

  const handleUpdateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 3) {
      toast.warning('Commande minimum : 3 ml par référence');
      return;
    }
    const result = await updateQuantity(productId, newQuantity);
    if (!result.success) {
      toast.error('Erreur lors de la mise à jour de la quantité');
    }
  };

  const handleRemove = async (productId, productName) => {
    const result = await removeFromCart(productId);
    if (result.success) {
      toast.success('Produit retiré du panier', {
        description: productName
      });
    } else {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading && !cart) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Chargement du panier...</div>
      </div>
    );
  }

  const isEmpty = !cart || !cart.items || cart.items.length === 0;

  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <section className="bg-gray-900 text-white py-12 md:py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">Mon Panier</h1>
          <p className="text-base sm:text-xl">Vos produits sélectionnés</p>
        </div>
      </section>

      {/* Cart content */}
      <section className="container mx-auto px-4 py-10 md:py-16">
        {isEmpty ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🛒</div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Votre panier est vide</h2>
            <p className="text-gray-600 mb-8">Découvrez nos produits et ajoutez-en à votre panier</p>
            <Link
              to="/produits"
              className="bg-[#E42313] text-white px-6 sm:px-8 py-3 sm:py-4 rounded hover:bg-[#C11A0F] transition-colors inline-flex items-center gap-2 font-semibold text-base sm:text-lg"
            >
              Voir nos produits
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Cart items */}
            <div className="lg:col-span-2">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Articles ({cart.items.length})</h2>
              <div className="space-y-4">
                {cart.items.map((item) => (
                  <div key={item.productId} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 flex items-center gap-3 sm:gap-4 flex-wrap sm:flex-nowrap">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-cover rounded shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">{item.name}</h3>
                      <p className="text-[#E42313] font-bold text-base sm:text-lg">{item.price.toFixed(2)} € <span className="text-xs text-gray-500 font-normal">/ ml</span></p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleUpdateQuantity(item.productId, item.quantity - 1)}
                        disabled={item.quantity <= 3}
                        className="w-8 h-8 rounded border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <div className="w-16 text-center">
                        <div className="font-semibold">{item.quantity}</div>
                        <div className="text-[10px] text-gray-500">ml</div>
                      </div>
                      <button
                        onClick={() => handleUpdateQuantity(item.productId, item.quantity + 1)}
                        className="w-8 h-8 rounded border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900 text-lg">{(item.price * item.quantity).toFixed(2)} €</p>
                    </div>
                    <button
                      onClick={() => handleRemove(item.productId, item.name)}
                      className="text-red-500 hover:text-red-700 p-2"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Order summary */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-lg p-5 sm:p-6 lg:sticky lg:top-24">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Récapitulatif</h2>
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-700">
                    <span>Sous-total</span>
                    <span>{cart.totalAmount.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>Retrait sur place</span>
                    <span className="text-green-600 font-semibold">Gratuit</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between text-xl font-bold text-gray-900">
                      <span>Total</span>
                      <span className="text-[#E42313]">{cart.totalAmount.toFixed(2)} €</span>
                    </div>
                  </div>
                </div>
                <Link
                  to="/checkout"
                  data-testid="cart-checkout-btn"
                  className="w-full bg-[#E42313] text-white px-6 py-4 rounded hover:bg-[#C11A0F] transition-colors font-semibold text-lg mb-4 block text-center"
                >
                  Commander
                </Link>
                <Link
                  to="/produits"
                  className="block text-center text-[#E42313] font-semibold hover:underline"
                >
                  Continuer mes achats
                </Link>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Cart;
